import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

function slugify(text) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

export default function EventBuilder({ eventId, pitchEventId, onBack }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('basic');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedSlug, setSavedSlug] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    event_name: '', tagline: '', description: '', start_date: '', end_date: '',
    time_commitment: '', event_password: '', hero_image_url: '', logo_image_url: '',
    company_headline: '', company_value_prop: '', calendly_booking_link: '',
    booking_cta_text: 'Reserve Your Time Slot', company_price: 'FREE',
    inventor_acceptance_msg: '', inventor_instructions: '', inventor_cta_text: '', inventor_cta_link: '',
    submission_terms: 'You understand that the event organizer and PlayVentr have worked on many product inventions and brands in the past, are currently working on products and brands, and shall continue to do so in the future. Any ideas and products you discuss or present may be similar to those already in development. You understand this and agree you will not have any legal or financial recourse whatsoever against the event organizer, PlayVentr, their employees, officers, affiliates, and subcontractors; you freely agree to assume this risk. You will maintain full ownership of your products and brands.',
    public_headline: '', public_subheadline: '', about_host: '',
    how_it_works: [{ title: '', description: '' }],
    past_attendees: [''],
    featured_inventors: [{ name: '', title: '', bio: '', photo_url: '' }],
    faq: [{ question: '', answer: '' }],
    intake_categories: [
      'Arts + Crafts / Girls Activity', 'Action Figures', 'Fashion Dolls', 'Pre-School',
      'Electronics', 'Family Games', 'Party Games', 'Sports / Outdoor Play',
      'Feature Plush', 'Collectibles', 'Show / IP Concepts', 'Books',
    ],
    slug: '', status: 'draft',
    // Pricing
    sponsorship_enabled: false,
    sponsorship_tiers: [
      { id: 'innovator', name: 'Innovator Sponsor', price_cents: 25000, description: 'Support emerging toy creators and gain visibility at the event.', perks: ['Logo on event page', 'Mentioned in opening remarks'], max_sponsors: null },
      { id: 'visionary', name: 'Visionary Sponsor', price_cents: 50000, description: 'Premium sponsorship with priority access and featured branding.', perks: ['Logo on event page', 'Featured in email invitations', 'Priority pitch scheduling', 'Post-event recap feature'], max_sponsors: null },
    ],
    inventor_fee_enabled: false,
    inventor_fee_cents: 9900,
    inventor_fee_label: 'Founding Inventor Rate',
    inventor_fee_description: "As we launch our new AI-powered matching platform, we're offering a special founding rate. Be among the first inventors to experience smarter, faster matching.",
    inventor_payment_url: '',
  });

  useEffect(() => {
    if (eventId) loadEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const loadEvent = async () => {
    const { data } = await supabase
      .from('event_pages')
      .select('*')
      .eq('id', eventId)
      .single();
    if (data) {
      setForm({
        ...data,
        how_it_works: data.how_it_works || [{ title: '', description: '' }],
        past_attendees: data.past_attendees || [''],
        featured_inventors: data.featured_inventors || [{ name: '', title: '', bio: '', photo_url: '' }],
        faq: data.faq || [{ question: '', answer: '' }],
        intake_categories: data.intake_categories || form.intake_categories,
        sponsorship_enabled: data.sponsorship_enabled || false,
        sponsorship_tiers: data.sponsorship_tiers || form.sponsorship_tiers,
        inventor_fee_enabled: data.inventor_fee_enabled || false,
        inventor_fee_cents: data.inventor_fee_cents ?? 9900,
        inventor_fee_label: data.inventor_fee_label || 'Founding Inventor Rate',
        inventor_fee_description: data.inventor_fee_description || form.inventor_fee_description,
        inventor_payment_url: data.inventor_payment_url || '',
        submission_terms: data.submission_terms || form.submission_terms,
      });
      setSavedSlug(data.slug);
    }
  };

  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const updateArrayItem = (key, index, field, val) => {
    setForm(prev => {
      const arr = [...prev[key]];
      if (typeof arr[index] === 'object') {
        arr[index] = { ...arr[index], [field]: val };
      } else {
        arr[index] = val;
      }
      return { ...prev, [key]: arr };
    });
  };

  const addArrayItem = (key, template) => {
    setForm(prev => ({ ...prev, [key]: [...prev[key], template] }));
  };

  const removeArrayItem = (key, index) => {
    setForm(prev => ({ ...prev, [key]: prev[key].filter((_, i) => i !== index) }));
  };

  const uploadImage = async (file, field) => {
    setUploading(true);
    const ext = file.name.split('.').pop();
    const path = `events/${user.id}/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('event-assets').upload(path, file);
    if (error) {
      alert('Upload failed: ' + error.message);
      setUploading(false);
      return;
    }
    const { data: urlData } = supabase.storage.from('event-assets').getPublicUrl(path);
    update(field, urlData.publicUrl);
    setUploading(false);
  };

  const handleSave = async (publish = false) => {
    setSaving(true);
    const slug = form.slug || slugify(form.event_name);
    const payload = {
      ...form,
      slug,
      status: publish ? 'published' : form.status,
      organizer_id: user.id,
      pitch_event_id: pitchEventId || form.pitch_event_id || null,
      how_it_works: form.how_it_works.filter(s => s.title),
      past_attendees: form.past_attendees.filter(a => a),
      featured_inventors: form.featured_inventors.filter(i => i.name),
      faq: form.faq.filter(f => f.question),
    };
    delete payload.id;
    delete payload.created_at;
    delete payload.updated_at;

    let result;
    if (eventId) {
      result = await supabase.from('event_pages').update(payload).eq('id', eventId).select().single();
    } else {
      result = await supabase.from('event_pages').insert(payload).select().single();
    }

    if (result.error) {
      alert('Save failed: ' + result.error.message);
    } else {
      setSaved(true);
      setSavedSlug(slug);
      if (publish) update('status', 'published');
    }
    setSaving(false);
  };

  const tabs = [
    { key: 'basic', label: 'Basic Info' },
    { key: 'public', label: 'Public Page' },
    { key: 'company', label: 'Company Page' },
    { key: 'inventor', label: 'Inventor Page' },
    { key: 'pricing', label: 'Pricing' },
  ];

  const inputCls = "w-full px-4 py-3 rounded-input border-2 border-vault-border text-sm font-body text-vault-navy bg-white/70 focus:border-vault-gold focus:outline-none transition-colors";
  const labelCls = "block text-[12px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5";

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        {onBack && (
          <button onClick={onBack} className="text-vault-gold hover:text-vault-gold-dark text-sm font-semibold">
            ← Back to Events
          </button>
        )}
        <h1 className="font-heading text-3xl text-vault-navy">
          {eventId ? 'Edit Event' : 'Create Event'}
        </h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-btn p-1 max-w-xl">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`flex-1 py-2.5 px-4 rounded-[10px] text-sm font-semibold transition-all ${
              activeTab === t.key
                ? 'bg-white text-vault-navy shadow-btn'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="glass p-7 max-w-3xl">
        {/* BASIC INFO TAB */}
        {activeTab === 'basic' && (
          <div className="space-y-5">
            <div>
              <label className={labelCls}>Event Name *</label>
              <input className={inputCls} value={form.event_name} onChange={e => { update('event_name', e.target.value); if (!form.slug) update('slug', slugify(e.target.value)); }} />
            </div>
            <div>
              <label className={labelCls}>URL Slug</label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400">/event/</span>
                <input className={inputCls + (eventId ? " bg-gray-100 cursor-not-allowed" : "")} value={form.slug || slugify(form.event_name)} onChange={e => update('slug', e.target.value)} readOnly={!!eventId} />
              </div>
              {eventId && form.slug && (
                <p className="text-[11px] text-gray-400 mt-1">URL cannot be changed after creation. Current page: <a href={`/event/${form.slug}`} target="_blank" rel="noreferrer" className="text-vault-gold hover:underline">/event/{form.slug}</a></p>
              )}
            </div>
            <div>
              <label className={labelCls}>Tagline</label>
              <input className={inputCls} value={form.tagline} onChange={e => update('tagline', e.target.value)} placeholder="e.g. Where toy creators meet top buyers" />
            </div>
            <div>
              <label className={labelCls}>Description</label>
              <textarea className={inputCls + " min-h-[120px]"} value={form.description} onChange={e => update('description', e.target.value)} rows={5} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Start Date</label>
                <input type="date" className={inputCls} value={form.start_date} onChange={e => update('start_date', e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>End Date</label>
                <input type="date" className={inputCls} value={form.end_date} onChange={e => update('end_date', e.target.value)} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Time Commitment</label>
              <input className={inputCls} value={form.time_commitment} onChange={e => update('time_commitment', e.target.value)} placeholder="e.g. Just 30 minutes" />
            </div>
            <div>
              <label className={labelCls}>Event Password (optional)</label>
              <input className={inputCls} value={form.event_password} onChange={e => update('event_password', e.target.value)} placeholder="Leave blank for no password" />
            </div>
            <div>
              <label className={labelCls}>Hero Image</label>
              {form.hero_image_url && <img src={form.hero_image_url} alt="Hero" className="w-full h-40 object-cover rounded-btn mb-2" />}
              <input type="file" accept="image/*" onChange={e => e.target.files[0] && uploadImage(e.target.files[0], 'hero_image_url')} disabled={uploading} />
            </div>
            <div>
              <label className={labelCls}>Logo / Banner Image</label>
              {form.logo_image_url && <img src={form.logo_image_url} alt="Logo" className="h-16 rounded mb-2" />}
              <input type="file" accept="image/*" onChange={e => e.target.files[0] && uploadImage(e.target.files[0], 'logo_image_url')} disabled={uploading} />
            </div>
            <div>
              <label className={labelCls}>Intake Form Categories</label>
              <p className="text-xs text-gray-400 mb-2">These categories appear on the company intake form. Add or remove as needed.</p>
              {form.intake_categories.map((cat, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <input className={inputCls} value={cat} onChange={e => updateArrayItem('intake_categories', i, null, e.target.value)} />
                  <button onClick={() => removeArrayItem('intake_categories', i)} className="text-vault-error text-sm font-semibold px-2">✕</button>
                </div>
              ))}
              <button onClick={() => addArrayItem('intake_categories', '')} className="text-vault-gold text-sm font-semibold">+ Add Category</button>
            </div>
          </div>
        )}

        {/* PUBLIC PAGE TAB */}
        {activeTab === 'public' && (
          <div className="space-y-5">
            <div>
              <label className={labelCls}>Public Headline</label>
              <input className={inputCls} value={form.public_headline} onChange={e => update('public_headline', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Public Subheadline</label>
              <input className={inputCls} value={form.public_subheadline} onChange={e => update('public_subheadline', e.target.value)} />
            </div>

            <div>
              <label className={labelCls}>How It Works (up to 5 steps)</label>
              {form.how_it_works.map((step, i) => (
                <div key={i} className="border border-vault-border rounded-btn p-4 mb-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold text-vault-navy">Step {i + 1}</span>
                    {form.how_it_works.length > 1 && (
                      <button onClick={() => removeArrayItem('how_it_works', i)} className="text-vault-error text-sm">Remove</button>
                    )}
                  </div>
                  <input className={inputCls + " mb-2"} placeholder="Step title" value={step.title} onChange={e => updateArrayItem('how_it_works', i, 'title', e.target.value)} />
                  <textarea className={inputCls} placeholder="Step description" value={step.description} onChange={e => updateArrayItem('how_it_works', i, 'description', e.target.value)} rows={2} />
                </div>
              ))}
              {form.how_it_works.length < 5 && (
                <button onClick={() => addArrayItem('how_it_works', { title: '', description: '' })} className="text-vault-gold text-sm font-semibold">+ Add Step</button>
              )}
            </div>

            <div>
              <label className={labelCls}>Featured Inventors</label>
              {form.featured_inventors.map((inv, i) => (
                <div key={i} className="border border-vault-border rounded-btn p-4 mb-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold text-vault-navy">Inventor {i + 1}</span>
                    {form.featured_inventors.length > 1 && (
                      <button onClick={() => removeArrayItem('featured_inventors', i)} className="text-vault-error text-sm">Remove</button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-2">
                    <input className={inputCls} placeholder="Name" value={inv.name} onChange={e => updateArrayItem('featured_inventors', i, 'name', e.target.value)} />
                    <input className={inputCls} placeholder="Title / Company" value={inv.title} onChange={e => updateArrayItem('featured_inventors', i, 'title', e.target.value)} />
                  </div>
                  <textarea className={inputCls + " mb-2"} placeholder="Short bio" value={inv.bio} onChange={e => updateArrayItem('featured_inventors', i, 'bio', e.target.value)} rows={2} />
                  <input className={inputCls} placeholder="Photo URL" value={inv.photo_url} onChange={e => updateArrayItem('featured_inventors', i, 'photo_url', e.target.value)} />
                </div>
              ))}
              <button onClick={() => addArrayItem('featured_inventors', { name: '', title: '', bio: '', photo_url: '' })} className="text-vault-gold text-sm font-semibold">+ Add Inventor</button>
            </div>

            <div>
              <label className={labelCls}>Past Attendees / Logos</label>
              {form.past_attendees.map((att, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <input className={inputCls} placeholder="Company name" value={att} onChange={e => updateArrayItem('past_attendees', i, null, e.target.value)} />
                  {form.past_attendees.length > 1 && (
                    <button onClick={() => removeArrayItem('past_attendees', i)} className="text-vault-error text-sm px-2">✕</button>
                  )}
                </div>
              ))}
              <button onClick={() => addArrayItem('past_attendees', '')} className="text-vault-gold text-sm font-semibold">+ Add Attendee</button>
            </div>

            <div>
              <label className={labelCls}>About the Host</label>
              <textarea className={inputCls + " min-h-[100px]"} value={form.about_host} onChange={e => update('about_host', e.target.value)} rows={4} />
            </div>

            <div>
              <label className={labelCls}>FAQ</label>
              {form.faq.map((item, i) => (
                <div key={i} className="border border-vault-border rounded-btn p-4 mb-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold text-vault-navy">FAQ {i + 1}</span>
                    {form.faq.length > 1 && (
                      <button onClick={() => removeArrayItem('faq', i)} className="text-vault-error text-sm">Remove</button>
                    )}
                  </div>
                  <input className={inputCls + " mb-2"} placeholder="Question" value={item.question} onChange={e => updateArrayItem('faq', i, 'question', e.target.value)} />
                  <textarea className={inputCls} placeholder="Answer" value={item.answer} onChange={e => updateArrayItem('faq', i, 'answer', e.target.value)} rows={2} />
                </div>
              ))}
              <button onClick={() => addArrayItem('faq', { question: '', answer: '' })} className="text-vault-gold text-sm font-semibold">+ Add FAQ</button>
            </div>
          </div>
        )}

        {/* COMPANY PAGE TAB */}
        {activeTab === 'company' && (
          <div className="space-y-5">
            <div>
              <label className={labelCls}>Company Invitation Headline</label>
              <input className={inputCls} value={form.company_headline} onChange={e => update('company_headline', e.target.value)} placeholder="e.g. You're Invited to Meet the Next Big Toy Creators" />
            </div>
            <div>
              <label className={labelCls}>Company Value Proposition</label>
              <textarea className={inputCls + " min-h-[100px]"} value={form.company_value_prop} onChange={e => update('company_value_prop', e.target.value)} rows={4} placeholder="Why should companies attend?" />
            </div>
            <div>
              <label className={labelCls}>Calendly Booking Link</label>
              <input className={inputCls} value={form.calendly_booking_link} onChange={e => update('calendly_booking_link', e.target.value)} placeholder="https://calendly.com/your-link" />
            </div>
            <div>
              <label className={labelCls}>Booking CTA Button Text</label>
              <input className={inputCls} value={form.booking_cta_text} onChange={e => update('booking_cta_text', e.target.value)} />
            </div>
          </div>
        )}

        {/* INVENTOR PAGE TAB */}
        {activeTab === 'inventor' && (
          <div className="space-y-5">
            <div>
              <label className={labelCls}>Submission Terms & Conditions *</label>
              <p className="text-xs text-gray-400 mb-2">Inventors must agree to these terms before submitting. Customize per event. A recommended default is provided.</p>
              <textarea className={inputCls + " min-h-[140px]"} value={form.submission_terms} onChange={e => update('submission_terms', e.target.value)} rows={6} placeholder="Enter the terms and conditions inventors must agree to before submitting their idea..." />
              {!form.submission_terms && (
                <p className="text-xs mt-1" style={{ color: '#F97066' }}>Required — inventors cannot submit without terms to accept</p>
              )}
            </div>
            <div>
              <label className={labelCls}>Acceptance Message</label>
              <textarea className={inputCls + " min-h-[100px]"} value={form.inventor_acceptance_msg} onChange={e => update('inventor_acceptance_msg', e.target.value)} rows={4} placeholder="Congratulations! You've been selected..." />
            </div>
            <div>
              <label className={labelCls}>Prep Instructions</label>
              <textarea className={inputCls + " min-h-[100px]"} value={form.inventor_instructions} onChange={e => update('inventor_instructions', e.target.value)} rows={4} placeholder="What inventors should prepare..." />
            </div>
            <div>
              <label className={labelCls}>CTA Button Text</label>
              <input className={inputCls} value={form.inventor_cta_text} onChange={e => update('inventor_cta_text', e.target.value)} placeholder="e.g. Submit Your Pitch Deck" />
            </div>
            <div>
              <label className={labelCls}>CTA Button Link</label>
              <input className={inputCls} value={form.inventor_cta_link} onChange={e => update('inventor_cta_link', e.target.value)} placeholder="https://..." />
            </div>
          </div>
        )}

        {/* PRICING TAB */}
        {activeTab === 'pricing' && (
          <div className="space-y-8">
            {/* COMPANY SPONSORSHIP */}
            <div>
              <h3 className="font-heading text-lg text-vault-navy mb-4">Company Sponsorship</h3>
              <label className="flex items-center gap-3 cursor-pointer mb-5">
                <input
                  type="checkbox"
                  checked={form.sponsorship_enabled}
                  onChange={e => update('sponsorship_enabled', e.target.checked)}
                  className="w-5 h-5 accent-amber-500"
                />
                <div>
                  <div className="text-sm font-semibold text-vault-navy">Enable sponsorship tiers</div>
                  <div className="text-xs text-gray-400">Companies can select a sponsorship level during registration</div>
                </div>
              </label>

              {form.sponsorship_enabled && (
                <div className="space-y-4">
                  {form.sponsorship_tiers.map((tier, ti) => (
                    <div key={ti} className="border-2 border-vault-border rounded-btn p-5">
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-sm font-semibold text-vault-navy">Tier {ti + 1}</span>
                        {form.sponsorship_tiers.length > 1 && (
                          <button onClick={() => {
                            setForm(prev => ({ ...prev, sponsorship_tiers: prev.sponsorship_tiers.filter((_, i) => i !== ti) }));
                          }} className="text-vault-error text-sm font-semibold">Remove</button>
                        )}
                      </div>
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className={labelCls}>Tier Name</label>
                            <input className={inputCls} value={tier.name} onChange={e => {
                              const tiers = [...form.sponsorship_tiers];
                              tiers[ti] = { ...tiers[ti], name: e.target.value, id: e.target.value.toLowerCase().replace(/\s+/g, '-') };
                              update('sponsorship_tiers', tiers);
                            }} />
                          </div>
                          <div>
                            <label className={labelCls}>Price ($)</label>
                            <input type="number" className={inputCls} value={tier.price_cents / 100} onChange={e => {
                              const tiers = [...form.sponsorship_tiers];
                              tiers[ti] = { ...tiers[ti], price_cents: Math.round(parseFloat(e.target.value || 0) * 100) };
                              update('sponsorship_tiers', tiers);
                            }} />
                          </div>
                        </div>
                        <div>
                          <label className={labelCls}>Description</label>
                          <textarea className={inputCls} value={tier.description} onChange={e => {
                            const tiers = [...form.sponsorship_tiers];
                            tiers[ti] = { ...tiers[ti], description: e.target.value };
                            update('sponsorship_tiers', tiers);
                          }} rows={2} />
                        </div>
                        <div>
                          <label className={labelCls}>Max Sponsors (blank = unlimited)</label>
                          <input type="number" className={inputCls + " max-w-[200px]"} value={tier.max_sponsors || ''} onChange={e => {
                            const tiers = [...form.sponsorship_tiers];
                            tiers[ti] = { ...tiers[ti], max_sponsors: e.target.value ? parseInt(e.target.value) : null };
                            update('sponsorship_tiers', tiers);
                          }} placeholder="Unlimited" />
                        </div>
                        <div>
                          <label className={labelCls}>Perks</label>
                          {(tier.perks || []).map((perk, pi) => (
                            <div key={pi} className="flex items-center gap-2 mb-2">
                              <input className={inputCls} value={perk} onChange={e => {
                                const tiers = [...form.sponsorship_tiers];
                                const perks = [...(tiers[ti].perks || [])];
                                perks[pi] = e.target.value;
                                tiers[ti] = { ...tiers[ti], perks };
                                update('sponsorship_tiers', tiers);
                              }} />
                              <button onClick={() => {
                                const tiers = [...form.sponsorship_tiers];
                                tiers[ti] = { ...tiers[ti], perks: tiers[ti].perks.filter((_, i) => i !== pi) };
                                update('sponsorship_tiers', tiers);
                              }} className="text-vault-error text-sm px-2">✕</button>
                            </div>
                          ))}
                          <button onClick={() => {
                            const tiers = [...form.sponsorship_tiers];
                            tiers[ti] = { ...tiers[ti], perks: [...(tiers[ti].perks || []), ''] };
                            update('sponsorship_tiers', tiers);
                          }} className="text-vault-gold text-sm font-semibold">+ Add Perk</button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => update('sponsorship_tiers', [...form.sponsorship_tiers, {
                      id: `tier-${Date.now()}`, name: '', price_cents: 0, description: '', perks: [], max_sponsors: null,
                    }])}
                    className="text-vault-gold text-sm font-semibold"
                  >
                    + Add Tier
                  </button>
                </div>
              )}
            </div>

            {/* INVENTOR FEE */}
            <div className="border-t border-vault-border pt-8">
              <h3 className="font-heading text-lg text-vault-navy mb-4">Inventor Application Fee</h3>
              <label className="flex items-center gap-3 cursor-pointer mb-5">
                <input
                  type="checkbox"
                  checked={form.inventor_fee_enabled}
                  onChange={e => update('inventor_fee_enabled', e.target.checked)}
                  className="w-5 h-5 accent-amber-500"
                />
                <div>
                  <div className="text-sm font-semibold text-vault-navy">Charge inventor application fee</div>
                  <div className="text-xs text-gray-400">Inventors will be redirected to your payment link before submitting</div>
                </div>
              </label>

              {form.inventor_fee_enabled && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Fee Amount ($)</label>
                      <input type="number" className={inputCls} value={form.inventor_fee_cents / 100} onChange={e => update('inventor_fee_cents', Math.round(parseFloat(e.target.value || 0) * 100))} />
                    </div>
                    <div>
                      <label className={labelCls}>Fee Label</label>
                      <input className={inputCls} value={form.inventor_fee_label} onChange={e => update('inventor_fee_label', e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Fee Description</label>
                    <textarea className={inputCls} value={form.inventor_fee_description} onChange={e => update('inventor_fee_description', e.target.value)} rows={3} />
                  </div>
                  <div className="bg-indigo-50 border border-indigo-200 rounded-btn p-4">
                    <p className="text-sm text-indigo-700 font-medium">Payment via Stripe Checkout</p>
                    <p className="text-xs text-indigo-600 mt-1">Inventors will complete payment through Stripe before accessing the submission form. The fee amount above is charged automatically.</p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-btn p-4">
                    <p className="text-sm text-amber-700 font-medium">Non-refundable disclaimer (always shown to inventors):</p>
                    <p className="text-sm text-amber-600 mt-1 italic">"This fee is non-refundable regardless of application outcome."</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Save Actions */}
        <div className="mt-8 pt-6 border-t border-vault-border flex items-center gap-4">
          <button
            onClick={() => handleSave(false)}
            disabled={saving || !form.event_name}
            className="px-6 py-2.5 rounded-btn text-sm font-semibold border-2 border-vault-border text-vault-navy hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            onClick={() => handleSave(true)}
            disabled={saving || !form.event_name}
            className="px-6 py-2.5 rounded-btn text-sm font-semibold text-white shadow-btn transition-all hover:shadow-lg disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
          >
            {saving ? 'Publishing...' : 'Publish Event'}
          </button>
        </div>

        {/* Success / Links */}
        {saved && savedSlug && (
          <div className="mt-6 p-5 bg-emerald-50 border border-emerald-200 rounded-btn">
            <p className="text-emerald-700 font-semibold text-sm mb-3">Event saved successfully!</p>
            <div className="space-y-2 text-sm">
              <div><span className="text-gray-500">Public Page:</span> <a href={`/event/${savedSlug}`} target="_blank" rel="noreferrer" className="text-vault-gold font-semibold hover:underline">/event/{savedSlug}</a></div>
              <div><span className="text-gray-500">Company Page:</span> <a href={`/event/${savedSlug}/companies`} target="_blank" rel="noreferrer" className="text-vault-gold font-semibold hover:underline">/event/{savedSlug}/companies</a></div>
              <div><span className="text-gray-500">Inventor Page:</span> <a href={`/event/${savedSlug}/inventors`} target="_blank" rel="noreferrer" className="text-vault-gold font-semibold hover:underline">/event/{savedSlug}/inventors</a></div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
